import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoHomeOutline } from "react-icons/io5";
import { IoIosMenu } from "react-icons/io";
import { FaLayerGroup } from "react-icons/fa";
import { RiSlideshow3Line, RiPagesLine } from "react-icons/ri";
import { PiFlagBannerBold } from "react-icons/pi";
import { RxBoxModel } from "react-icons/rx";
import { BsBorderAll } from "react-icons/bs";
import { MdOutlineShoppingCart } from "react-icons/md";
import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";

function NewPanelPage({ language }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = language === "fa";
  const buttonClassName = isRTL ? "panel2_logout-fa" : "panel2_logout";
  const logoClassName = isRTL ? "panel2_logo-fa" : "panel2_logo";
  const menuItemClassName = isRTL ? "panel2-menu-items-fa" : "panel2-menu-items";
  const panelBase = `/admin/panel2/${language}`;

  useEffect(() => {
    const currentLang = location.pathname.includes("/fa") ? "fa" : "en";
    if (currentLang !== i18n.language) {
      i18n.changeLanguage(currentLang);
      document.body.dir = currentLang === "fa" ? "rtl" : "ltr";
      document.body.className = currentLang === "fa" ? "font_farsi" : "font_en";
    }
  }, [location.pathname, i18n]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleLanguageChange = (newLang) => {
    const currentPath = location.pathname;
    const pathParts = currentPath.split("/");
    const basePath = "/admin/panel2";
    if (pathParts.length > 3) {
      pathParts[3] = newLang;
      const newPath = pathParts.join("/");
      navigate(newPath);
    } else {
      navigate(`${basePath}/${newLang}`);
    }
  };

  const handleMenuItemClick = () => {
    if (windowWidth < 768) {
      setIsCollapsed(true);
    }
  };

  const isActive = (path) => {
    if (!path) {
      return location.pathname === panelBase || location.pathname === `${panelBase}/`;
    }
    return location.pathname === `${panelBase}/${path}`;
  };

  return (
    <div className={`panel2_container ${isRTL ? "rtl" : "ltr"}`}>
      <header className="panel2_header">
        <span className={logoClassName}>{t("panel2_logo")}</span>
        <div className="d-flex align-items-center gap-3">
          <div className="language-buttons">
            <button
              className={`lang-btn ${language === "en" ? "active" : ""}`}
              onClick={() => handleLanguageChange("en")}
            >
              English
            </button>
            <span className="p-2">|</span>
            <button
              className={`lang-btn ${language === "fa" ? "active" : ""}`}
              onClick={() => handleLanguageChange("fa")}
            >
              فارسی
            </button>
          </div>
          <button className={buttonClassName}>
            <Link to={language === "fa" ? "/fa" : "/en"}>
              <span>{t("backToWebsite")}</span>
            </Link>
          </button>
        </div>
      </header>

      <div className="panel2_main">
        <nav
          className={`panel2_sidebar ${isCollapsed ? "collapsed" : ""} ${isRTL ? "rtl" : "ltr"}`}
        >
          <div className="panel2_toggle-container">
            <button className="panel2_toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? "☰" : "×"}
            </button>
          </div>
          <ul className={`${menuItemClassName} ${isCollapsed ? "collapsed" : ""}`}>
            <li className={isActive("") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="">
                <IoHomeOutline /> <span>{t("dashboard")}</span>
              </Link>
            </li>
            <li className={isActive("menu") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="menu">
                <IoIosMenu /> <span>{t("menu")}</span>
              </Link>
            </li>
            <li className={isActive("group-page") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="group-page">
                <FaLayerGroup /> <span>{t("groupPage_group_page")}</span>
              </Link>
            </li>
            <li className={isActive("slideshow") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="slideshow">
                <RiSlideshow3Line /> <span>{t("slideShow")}</span>
              </Link>
            </li>
            <li className={isActive("banner") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="banner">
                <PiFlagBannerBold /> <span>{t("banner")}</span>
              </Link>
            </li>
            <li className={isActive("page-panel") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="page-panel">
                <RiPagesLine /> <span>{t("pages")}</span>
              </Link>
            </li>
            <li
              className={isActive("page-panel-test") ? "active" : ""}
              onClick={handleMenuItemClick}
            >
              <Link to="page-panel-test">
                <RiPagesLine /> <span>pages-test</span>
              </Link>
            </li>
            <li className={isActive("models") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="models">
                <RxBoxModel /> <span>{t("models")}</span>
              </Link>
            </li>
            <li className={isActive("orders") ? "active" : ""} onClick={handleMenuItemClick}>
              <Link to="orders">
                <MdOutlineShoppingCart /> <span>{t("orders")}</span>
              </Link>
            </li>
            <li className="dropdown" onClick={handleMenuItemClick}>
              <div className="dropdown-trigger">
                <BsBorderAll /> <span>{t("homePage")}</span>
              </div>
              <ul className="dropdown-menu-newPanel">
                <li className={isActive("page1") ? "active" : ""}>
                  <Link to="page1">{t("page1")}</Link>
                </li>
                <li className={isActive("page2") ? "active" : ""}>
                  <Link to="page2">{t("page2")}</Link>
                </li>
                <li className={isActive("page3") ? "active" : ""}>
                  <Link to="page3">{t("page3")}</Link>
                </li>
              </ul>
            </li>
          </ul>
          <div className="language-buttons-mobile">
            <button
              className={`lang-btn ${language === "en" ? "active" : ""}`}
              onClick={() => handleLanguageChange("en")}
            >
              English
            </button>
            <span className="p-2">|</span>
            <button
              className={`lang-btn ${language === "fa" ? "active" : ""}`}
              onClick={() => handleLanguageChange("fa")}
            >
              فارسی
            </button>
          </div>
        </nav>

        <main className="panel2_content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default NewPanelPage;
